/**
 * Urdhëresa e pagesës — faqosja e vetme që ushqen ekranin, Excel-in dhe PDF-në.
 *
 * Ky nuk është një listë e brendshme: është dokumenti që i dërgohet bankës, ndaj
 * mban kokën e kompanisë, xhirollogarinë prej së cilës paguhet, totalin dhe një
 * vend nënshkrimi. Rreshtat PA IBAN numërohen veçmas dhe shkruhen si shënim në
 * kokë — një urdhëresë ku dy rreshta s'kanë llogari nuk ekzekutohet dot, dhe
 * kjo duhet të dihet para se të dërgohet, jo pasi kthehet.
 *
 * Etiketat vijnë të përkthyera nga faqja (`t`), ndaj skedari del në gjuhën e
 * përdoruesit — asnjë varg i ngurtë këtu.
 */
import { exportSheetExcel, downloadSheetPdf, printSheet } from './reportSheetExport';

/** Rreshti i urdhëresës nga një rresht page — emrat e fushave ndryshojnë sipas burimit. */
export function creditorRow(w, i) {
  return {
    no:     i + 1,
    name:   w.CreditorName || w.FullName || `${w.FirstName || ''} ${w.LastName || ''}`.trim() || '—',
    nid:    w.PersonalNumber || w.PersonalId || '',
    iban:   w.CreditorIBAN || w.IBAN || w.BankAccount || '',
    job:    w.TypeOfJob || w.Position || '',
    dept:   w.DepartmentName || '',
    branch: w.Branch || w.BranchName || '',
    amount: Number(w.Amount ?? w.neto ?? w.NetSalary ?? 0) || 0,
  };
}

export function creditorOrderLayout(items, { company = {}, period = '', t, fileBase = 'urdheresa-e-pageses' } = {}) {
  const rows    = (items || []).map(creditorRow);
  const total   = rows.reduce((a, r) => a + r.amount, 0);
  const noIban  = rows.filter((r) => !r.iban).length;
  const title   = t('reports.creditorMonthly');

  return {
    title: title.toUpperCase(),
    logo: company.Logo || null,
    left: [
      company.CompanyName || company.name || '—',
      [company.NUI && `NUI: ${company.NUI}`, company.FiscalNumber && `NRF: ${company.FiscalNumber}`].filter(Boolean).join('   ·   '),
      company.Address || '',
      company.Phone ? `Tel: ${company.Phone}` : '',
    ],
    right: [
      `${t('report.period')}: ${period}`,
      `${t('report.creditors')}: ${rows.length}`,
      company.BankName ? `${t('report.bank')}: ${company.BankName}` : '',
      company.BankAccount ? `IBAN: ${company.BankAccount}` : '',
    ],
    // Shënimi shkruhet vetëm kur ka çka të thotë — një rresht "0 pa IBAN" do të
    // ishte zhurmë në çdo dokument të rregullt.
    note: noIban ? `${noIban} ${t('report.ibanMissingHint')}` : '',
    columns: [
      { key: 'no',     label: t('report.nr'),           w:  5, align: 'center', pdfW: 8 },
      { key: 'name',   label: t('report.creditorName'), w: 28 },
      { key: 'nid',    label: t('report.personalNr'),   w: 16 },
      { key: 'iban',   label: 'IBAN',                   w: 26 },
      { key: 'job',    label: t('report.jobType'),      w: 20, muted: true },
      { key: 'dept',   label: t('report.department'),   w: 18, muted: true },
      { key: 'branch', label: t('report.branch'),       w: 14, muted: true },
      { key: 'amount', label: t('report.amount'),       w: 14, money: true, bold: true },
    ],
    body: rows,
    totals: { amount: total },
    totalLabel: t('report.totalRow'),
    summaryTitle: t('report.summaryTitle'),
    summary: [
      { label: t('report.creditors'),     value: rows.length },
      { label: t('report.averageAmount'), value: rows.length ? total / rows.length : 0, money: true },
      { label: t('report.missingIban'),   value: noIban },
      { label: t('report.payableTotal'),  value: total, money: true },
    ],
    signature: {
      left:  `${t('report.date')}: ______________`,
      right: t('report.authorizedSignature'),
    },
    footer: [company.CompanyName || company.name, title, period].filter(Boolean).join(' · '),
    sheetName: title,
    fileBase,
    orientation: 'landscape',
  };
}

export const exportCreditorExcel = (L) => exportSheetExcel(L);
export const exportCreditorPdf   = (L) => downloadSheetPdf(L);
export const printCreditorOrder  = (L) => printSheet(L);
